import { useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CurrentUser,
  QAlert,
  QBox,
  QButton,
  QCollapse,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QMultiSelect,
  QRadio,
  QRadioGroup,
  QSelect,
  QStack,
  QText,
  QTextarea,
  useToastProvider,
} from '@qualio/ui-components';

import { changeControlApi } from '../../../../../api/changeControl';
import { documentApi } from '../../../../../api/document';
import { QualioDocument } from '../../../../../api/model/document';
import {
  CreatePeriodicReviewRequest,
  PeriodicReview,
} from '../../../../../api/model/periodicReview';
import {
  MedtechCompanyUserGroup,
  MedtechUserV2,
} from '../../../../../api/user';

import { redirectTo } from '../../../../../util/PageUtils';

import {
  DOCUMENT_SUBMIT_PERIODIC_REVIEW_ERROR,
  logCustomError,
} from '../../../../../messages/LogErrorMessages';
import {
  ChangeControlDTO,
  ChangeControlTemplateDTO,
} from '../../../../../types/DocumentCreate';
import {
  isNormalUsergroup,
  isQualityUsergroup,
} from '../../../../../util/CurrentUser';
import styles from './PeriodicReviewModal.module.css';
import { BadApproversProvidedErrorSchema } from './types';

export type PeriodicReviewModalProps = {
  onClose: () => void;
  isOpen: boolean;
  currentUser: CurrentUser;
  qualioDocument: QualioDocument;
  periodicReview?: PeriodicReview;
  users: MedtechUserV2[];
  submissionCallback: () => void;
};

enum PeriodicReviewUpdateStatus {
  UPDATE = 'update',
  DO_NOT_UPDATE = 'no_update',
}

type PeriodicReviewOption = {
  value: number | string | null;
  label: string;
};

type PeriodicReviewForm = {
  qualityApprovers: number[];
  otherApprovers: number[];
  comment: string;
  reviewPeriod: string | null;
};

const getCurrentApprovers = (
  qualioDocument: QualioDocument,
  type: MedtechCompanyUserGroup,
  users: MedtechUserV2[],
  periodicReview?: PeriodicReview,
) => {
  const { audit_groups: { approver_group } = {} } = qualioDocument;

  if (periodicReview) {
    return periodicReview.approvers
      .filter((approver) => {
        const user = users.find((item) => item.id === approver.id);
        return user?.usergroups.includes(type);
      })
      .map((approver) => approver.id);
  }

  if (approver_group) {
    return approver_group
      .filter(
        (approver) => approver?.usergroups.includes(type) && approver.active,
      )
      .map((approver) => approver?.id);
  }

  return [];
};

const getReviewPeriodOptions = (t: (key: string) => string) => {
  const options: PeriodicReviewOption[] = [
    { value: null, label: t('labels.none') },
  ];
  for (let i = 1; i <= 36; i++) {
    options.push({ value: i, label: `${i}` });
  }
  for (let g = 42; g <= 84; g += 6) {
    options.push({ value: g, label: `${g}` });
  }
  return options;
};

const getApproversOptions = (approvers: MedtechUserV2[]) => {
  return approvers.map((approver) => {
    return {
      value: approver.id,
      label: approver.full_name,
    };
  });
};

const sanitiseReviewPeriod = (reviewPeriodStr: string | null) => {
  return reviewPeriodStr === null
    ? reviewPeriodStr
    : Number.parseInt(reviewPeriodStr);
};

const getSanitisedValue = (value: any) => {
  return typeof value === 'object' && value !== null ? value?.value : value;
};

const getSanitisedApproversValues = (value: any[]) => {
  return value.map((val) => getSanitisedValue(val));
};

export const PeriodicReviewModal = ({
  onClose,
  isOpen,
  periodicReview,
  currentUser,
  qualioDocument,
  users,
  submissionCallback,
}: PeriodicReviewModalProps) => {
  const { t } = useTranslation();
  const [updateStatus, setUpdateStatus] = useState(
    PeriodicReviewUpdateStatus.UPDATE,
  );
  const [performingReviewAction, setPerformingReviewAction] = useState(false);

  const { showToast } = useToastProvider();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<PeriodicReviewForm>({
    values: {
      qualityApprovers: getCurrentApprovers(
        qualioDocument,
        'quality',
        users,
        periodicReview,
      ),
      otherApprovers: getCurrentApprovers(
        qualioDocument,
        'normal',
        users,
        periodicReview,
      ),
      comment: '',
      reviewPeriod:
        qualioDocument.review_period === null
          ? null
          : qualioDocument.review_period.toString(),
    },
  });

  const createNewDraft = async () => {
    setPerformingReviewAction(true);
    let newDoc: QualioDocument;
    let ccTab = '';
    try {
      if (periodicReview) {
        await documentApi.concludePeriodicReview(
          currentUser.companyId,
          qualioDocument.id,
        );
      }
      newDoc = await documentApi.createDraftFromEffectiveDoc(
        qualioDocument.company_id,
        qualioDocument.document_matrix_id,
      );
    } catch (e: unknown) {
      showToast({
        'data-cy': 'periodic-review-create-new-draft-error-toast',
        status: 'error',
        title: t('notifications.error'),
        description: t('documentActions.failedToCreateNewDraftFromPR'),
        id: 'periodic-review-create-new-draft-error-toast',
      });
      setPerformingReviewAction(false);
      return;
    }

    try {
      const changeControlTemplates: ChangeControlTemplateDTO[] =
        (await changeControlApi.fetchChangeControlTemplates()) ?? [];
      const changeControlTemplate = changeControlTemplates.find(
        (tmpl) => tmpl.qualio_managed && tmpl.status === 'effective',
      );
      if (changeControlTemplate) {
        await changeControlApi.createChangeControl(
          newDoc,
          Number(changeControlTemplate.id),
          { sections: changeControlTemplate.sections } as ChangeControlDTO,
        );
      }
    } catch (e: unknown) {
      ccTab = '?tab=change-control';
    }

    redirectTo(`/workspace/documents/${newDoc.id}${ccTab}`);
  };

  const startPeriodicReview: SubmitHandler<PeriodicReviewForm> = async (
    periodicReviewFormData,
  ) => {
    setPerformingReviewAction(true);
    const sanitsedPeriod = sanitiseReviewPeriod(
      periodicReviewFormData.reviewPeriod,
    );
    const periodicReviewData: CreatePeriodicReviewRequest = {
      comment: periodicReviewFormData.comment,
      suggested_review_period: sanitsedPeriod,
      approvers: [
        ...periodicReviewFormData.qualityApprovers,
        ...getSanitisedApproversValues(periodicReviewFormData.otherApprovers),
      ],
    };
    try {
      await documentApi.createPeriodicReview(
        currentUser,
        qualioDocument.id,
        periodicReviewData,
      );
      submissionCallback();
      onClose();
      const successDescription = periodicReview
        ? t('documentActions.existingPeriodicReviewCanceled')
        : t('periodicReview.successfullySubmittedForApproval');
      showToast({
        'data-cy': 'send-for-approval-button-notify-success-toast',
        status: 'success',
        title: t('notifications.sentForApproval'),
        description: successDescription,
        id: 'send-for-approval-button-notify-success-toast',
      });
    } catch (e: unknown) {
      logCustomError(DOCUMENT_SUBMIT_PERIODIC_REVIEW_ERROR, { error: e });
      const defaultErrorToastMsg = t(
        'periodicReview.failedToSubmitForApproval',
      );
      const badApproversError = BadApproversProvidedErrorSchema.safeParse(e);
      if (badApproversError.success) {
        // Parse userIds to names from audit_groups where they still exist
        const { userIds } = badApproversError.data;
        const invalidUsernames =
          qualioDocument.audit_groups?.approver_group
            ?.filter((approver) => userIds.includes(approver?.id))
            .map((approver) => approver.full_name || approver.email) || [];

        const badApproversToastMsg = invalidUsernames.length
          ? invalidUsernames.length === 1
            ? t('documentActions.approverLacksPermissions', {
                name: invalidUsernames[0],
              })
            : t('documentActions.approversLackPermissions', {
                names: invalidUsernames.join(', '),
              })
          : defaultErrorToastMsg;

        showToast({
          'data-cy': 'periodic-review-create-error-toast',
          status: 'error',
          title: t('notifications.error'),
          description: badApproversToastMsg,
          id: 'periodic-review-create-error-toast',
        });
      } else {
        showToast({
          'data-cy': 'periodic-review-create-error-toast',
          status: 'error',
          title: t('notifications.error'),
          description: defaultErrorToastMsg,
          id: 'periodic-review-create-error-toast',
        });
      }
    } finally {
      setPerformingReviewAction(false);
    }
  };

  const handlePeriodicReviewAction = () => {
    if (updateStatus === PeriodicReviewUpdateStatus.UPDATE) {
      void createNewDraft();
    } else {
      void handleSubmit(startPeriodicReview)();
    }
  };

  const handleRadioChange = (value: PeriodicReviewUpdateStatus) => {
    setUpdateStatus(value);
  };

  const actionText =
    updateStatus === PeriodicReviewUpdateStatus.UPDATE
      ? t('buttons.createNewDraft')
      : t('buttons.submitForApproval');

  const reviewPeriodOptions = useMemo(() => getReviewPeriodOptions(t), [t]);

  const qualityApproversOptions = useMemo(
    () => getApproversOptions(users.filter(isQualityUsergroup)),
    [users],
  );

  const otherApproversOptions = useMemo(
    () => getApproversOptions(users.filter(isNormalUsergroup)),
    [users],
  );

  return (
    <QModal
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      data-cy={'periodic-review-modal'}
    >
      <QModalHeader>
        <QText>{t('periodicReview.periodicReview')}</QText>
      </QModalHeader>
      <QModalBody>
        {periodicReview && (
          <QStack pb={4}>
            <QAlert
              description={t('periodicReview.byContinuingWillStopReview')}
              status="warning"
              title={t('messages.thisAffectsActivePeriodicReview')}
            />
          </QStack>
        )}
        <QStack>
          <QText>{t('messages.pleaseIndicateIfDocumentNeedsUpdate')}</QText>
        </QStack>
        <QRadioGroup onChange={handleRadioChange} value={updateStatus} pt={6}>
          <QStack spacing={3}>
            <QBox className={styles['periodic-review__form-control__wrapper']}>
              <QFormControl
                helper={t('periodicReview.newDraftVersionWillBeCreated')}
              >
                <QRadio
                  value={PeriodicReviewUpdateStatus.UPDATE}
                  data-cy="periodic-review-create-draft"
                >
                  {t('periodicReview.yesDocumentNeedsUpdating')}
                </QRadio>
              </QFormControl>
            </QBox>
            <QFormControl>
              <QRadio
                value={PeriodicReviewUpdateStatus.DO_NOT_UPDATE}
                data-cy="periodic-review-create-review"
              >
                {t('periodicReview.noDocumentDoesntNeedUpdate')}
              </QRadio>
            </QFormControl>
          </QStack>
        </QRadioGroup>
        <QCollapse
          isOpen={updateStatus === PeriodicReviewUpdateStatus.DO_NOT_UPDATE}
        >
          <QFormControl mt={4} label={t('forms.commentOptional')}>
            <Controller
              name="comment"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <QTextarea
                    onChange={onChange}
                    value={value}
                    data-cy={'periodic-review-comment'}
                    placeholder={t('periodicReview.reviewDocumentPlaceholder')}
                  />
                );
              }}
            ></Controller>
          </QFormControl>
          <QFormControl mt={4} label={t('forms.reviewPeriodMonths')}>
            <Controller
              name="reviewPeriod"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <QSelect
                    onChange={(val) => {
                      onChange(getSanitisedValue(val));
                    }}
                    value={getSanitisedValue(value)}
                    data-cy={'periodic-review-period'}
                    options={reviewPeriodOptions}
                  />
                );
              }}
            ></Controller>
          </QFormControl>
          <QFormControl
            label={t('labels.qualityApprovers')}
            isInvalid={Boolean(errors.qualityApprovers)}
            error={errors.qualityApprovers?.message}
            mt={4}
          >
            <Controller
              name="qualityApprovers"
              control={control}
              render={({ field: { onChange, value } }) => {
                const formProps = register('qualityApprovers', {
                  required: t('documentActions.youMustChooseMandatoryApprover'),
                });
                const { ref, ...sanitisedProps } = formProps;
                return (
                  <QMultiSelect
                    {...sanitisedProps}
                    onChange={(val: any) => {
                      onChange(getSanitisedApproversValues(val));
                    }}
                    value={getSanitisedApproversValues(value)}
                    data-cy={'periodic-review-quality-approvers'}
                    options={qualityApproversOptions}
                  />
                );
              }}
            ></Controller>
          </QFormControl>
          <QFormControl label={t('labels.otherApprovers')} mt={4}>
            <Controller
              name="otherApprovers"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <QMultiSelect
                    onChange={(val: any) => {
                      onChange(getSanitisedApproversValues(val));
                    }}
                    value={getSanitisedApproversValues(value)}
                    data-cy={'periodic-review-other-approvers'}
                    options={otherApproversOptions}
                  />
                );
              }}
            ></Controller>
          </QFormControl>
        </QCollapse>
      </QModalBody>
      <QModalActions>
        <QButton variant="outline" onClick={onClose}>
          {t('buttons.cancel')}
        </QButton>
        <QButton
          isLoading={performingReviewAction}
          onClick={handlePeriodicReviewAction}
          data-cy={'periodic-review-modal-confirmation'}
        >
          {actionText}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
